import type { PimFields, Variant } from "@thenounproject/lingo-core";
import { v4 as genUuid } from "uuid";
import { withSource } from "../productTypeFields";

export type ProductOptionData = {
  id: string;
  name: string;
  values: string[];
};

export type LocalVariant = {
  key: string;
  uuid?: string;
  optionValues: Record<string, string>;
  marketUuid: string | null;
  /** Full envelope map. `source` marks each entry as "override" or "inherited". */
  fields: PimFields;
  displayOrder: number;
  status?: string;
};

/**
 * Generate a stable key for a variant based on its option values and market.
 * Used for React keys and override lookup.
 */
export function createVariantKey(
  optionValues: Record<string, string>,
  marketUuid: string | null
): string {
  const optionStr = Object.keys(optionValues)
    .sort()
    .map(k => `${k}:${optionValues[k]}`)
    .join("|");
  return `${optionStr}||${marketUuid || "none"}`;
}

/**
 * Generate the cartesian product of all option values.
 * e.g., [{ name: "Size", values: ["500mg", "1g"] }] → [{ Size: "500mg" }, { Size: "1g" }]
 */
export function generateOptionCombinations(options: ProductOptionData[]): Record<string, string>[] {
  const valid = options.filter(o => o.name.trim() && o.values.some(v => v.trim()));
  if (!valid.length) return [];
  return valid.reduce<Record<string, string>[]>(
    (acc, opt) => {
      const trimmed = opt.values.filter(v => v.trim());
      return acc.flatMap(combo => trimmed.map(v => ({ ...combo, [opt.name.trim()]: v.trim() })));
    },
    [{}]
  );
}

/** Seed a variant's fields by copying the product's fields and marking every envelope inherited. */
function seedInheritedFields(productFields: PimFields): PimFields {
  const out: PimFields = {};
  for (const [id, v] of Object.entries(productFields)) {
    out[id] = withSource(v, "inherited");
  }
  return out;
}

/**
 * Build the full variant matrix from options × markets.
 * Preserves existing overrides and UUIDs when regenerating.
 */
export function buildVariantMatrix(
  options: ProductOptionData[],
  assignedMarketUuids: string[],
  existingVariants: LocalVariant[],
  existingApiVariants?: Variant[],
  productFields: PimFields = {}
): LocalVariant[] {
  const combinations = generateOptionCombinations(options);
  const hasMarkets = assignedMarketUuids.length > 0;
  if (!combinations.length && !hasMarkets) return [];

  const effectiveCombinations = combinations.length ? combinations : [{}];
  const marketTargets: (string | null)[] = hasMarkets ? assignedMarketUuids : [null];

  const existingByKey = new Map<string, LocalVariant>(existingVariants.map(v => [v.key, v]));
  const apiByKey = new Map<string, Variant>(
    (existingApiVariants ?? []).map(v => [createVariantKey(v.optionValues ?? {}, v.marketUuid), v])
  );

  const result: LocalVariant[] = [];
  let displayOrder = 0;

  for (const marketUuid of marketTargets) {
    for (const optionValues of effectiveCombinations) {
      const key = createVariantKey(optionValues, marketUuid);
      const existing = existingByKey.get(key);

      if (existing) {
        result.push({ ...existing, displayOrder: displayOrder++ });
        continue;
      }

      const apiVariant = apiByKey.get(key);

      result.push({
        key,
        uuid: apiVariant?.uuid,
        optionValues,
        marketUuid,
        fields: apiVariant?.fields ?? seedInheritedFields(productFields),
        displayOrder: displayOrder++,
        status: apiVariant?.status,
      });
    }
  }

  return result;
}

/**
 * Extract unique option definitions from existing API variants.
 */
export function extractOptionsFromVariants(variants: Variant[]): ProductOptionData[] {
  if (!variants?.length) return [];
  const optionMap = new Map<string, Set<string>>();
  variants.forEach(v => {
    Object.entries(v.optionValues || {}).forEach(([name, value]) => {
      if (!optionMap.has(name)) optionMap.set(name, new Set());
      optionMap.get(name)!.add(String(value));
    });
  });
  return Array.from(optionMap.entries()).map(([name, values]) => ({
    id: genUuid(),
    name,
    values: Array.from(values),
  }));
}

/**
 * Extract unique assigned market UUIDs from existing API variants.
 */
export function extractMarketUuidsFromVariants(variants: Variant[]): string[] {
  if (!variants?.length) return [];
  return [...new Set(variants.flatMap(v => (v.marketUuid ? [v.marketUuid] : [])))];
}

/**
 * Format a variant's option values into a display label.
 */
export function formatVariantLabel(optionValues: Record<string, string>): string {
  const values = Object.values(optionValues);
  return values.join(" / ") || "Defaults";
}
